// DDT-4096 — dismiss the on-screen keyboard once a dropdown choice is picked.
//
// A dropdown-backed field leaves focus on its input after a selection, so on a
// phone the soft keyboard stays up, covers the rest of the form and has to be
// closed by hand before the lawyer can carry on. Kevin asked for this as a form
// rule across every form we own rather than a per-form patch, so it is wired
// once here — the sibling of the same module in dd-fr-wp-theme, adapted to the
// two dropdown libraries this app uses:
//
//   * Tom Select fires a bubbling native `change` on the original <select> and
//     parks the instance on `element.tomselect`, so one document listener covers
//     every controller that builds one.
//   * Select2 fires `select2:select`, but through jQuery's own propagation, so
//     it needs a jQuery-delegated listener — a native one never sees it.
//
// Two deliberate limits, per the ticket's acceptance criteria:
//   * touch devices only. On desktop there is no keyboard to dismiss, and
//     blurring would fight keyboard navigation of the suggestion list.
//   * multi-select dropdowns are left alone — the lawyer is expected to keep
//     picking (areas of law, spoken languages, job-offer domains).

import $ from 'jquery';

const TOUCH_DEVICE_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * True on devices that raise a soft keyboard for a focused text field.
 *
 * Evaluated per event rather than once at load: a tablet with a keyboard
 * attached reports a fine pointer and shows no soft keyboard, and that can
 * change within the life of the page.
 */
function hasSoftKeyboard() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia(TOUCH_DEVICE_QUERY).matches;
}

function dismissKeyboard(element) {
    if (!element || typeof element.blur !== 'function') {
        return;
    }

    if (!hasSoftKeyboard()) {
        return;
    }

    element.blur();
}

// Tom Select — single-select only, and only while the control actually holds
// focus, so a programmatic setValue() during setup never steals focus from
// whatever the lawyer is typing in.
document.addEventListener('change', function (event) {
    const select = event.target;
    const tomSelect = select && select.tomselect;

    if (!tomSelect || !tomSelect.isFocused) {
        return;
    }

    if (tomSelect.settings.mode !== 'single') {
        return;
    }

    dismissKeyboard(tomSelect.focus_node || tomSelect.control_input);
});

// Select2 — same rule. `select.multiple` is the multi-select discriminator here.
// The field holding the keyboard is Select2's own search input inside the open
// dropdown; `select2:select` fires before the dropdown closes, so it is still
// there to blur.
$(document).on('select2:select', 'select', function (event) {
    if (event.target.multiple) {
        return;
    }

    dismissKeyboard(document.querySelector('.select2-container--open .select2-search__field'));
});

export default dismissKeyboard;
